import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EyeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  approveTPRMResponseApi,
  getTPRMResponseDetailApi,
  getTPRMResponsesApi,
  requestTPRMResponseChangesApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import DataTable from "../../common/DataTable";
import useTPRMDialog from "./useTPRMDialog";

const PAGE_SIZE = 20;
const EMPTY_VALUE = "—";
const STATUS_OPTIONS = [
  "Draft",
  "Sent",
  "In Progress",
  "Under Review",
  "Changes Requested",
  "Completed",
  "Expired",
];
const CRITICALITY_OPTIONS = ["Low", "Medium", "High", "Critical"];
const RISK_TIER_OPTIONS = ["Unassessed", "Low", "Medium", "High", "Critical"];

const toResponseFilterParams = (columnFilters) => {
  const params = {};
  columnFilters.forEach(({ id, value }) => {
    if (id === "submitted_at" && Array.isArray(value)) {
      if (value[0]) params.date_from = value[0];
      if (value[1]) params.date_to = value[1];
      return;
    }
    if (id === "assessment_id") {
      params.search = value;
      return;
    }
    params[id] = value;
  });
  return params;
};

const formatValue = (value) => (
  value === null || value === undefined || value === "" ? EMPTY_VALUE : String(value)
);

const formatDate = (value) => {
  if (!value) return EMPTY_VALUE;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? EMPTY_VALUE
    : new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        timeZone: "UTC",
      }).format(date);
};

const getResults = (payload) => ({
  results: Array.isArray(payload) ? payload : payload?.results || [],
  count: Number.isFinite(Number(payload?.count))
    ? Number(payload.count)
    : Array.isArray(payload)
      ? payload.length
      : 0,
});

const errorText = (error) => {
  const message = error?.response?.data?.detail || error?.message;
  return typeof message === "string" ? message : "The request failed. Please retry.";
};

const DetailField = ({ label, value }) => (
  <div>
    <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
    <dd className="mt-1 break-words font-medium text-slate-900 dark:text-white">
      {formatValue(value)}
    </dd>
  </div>
);

const ResponseDetailDialog = ({ assessmentId, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");
  const dialogRef = useTPRMDialog({ onClose });

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setDetail(await getTPRMResponseDetailApi(assessmentId));
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const review = async (action) => {
    if (action === "changes" && !reason.trim()) {
      setError("Enter a reason before requesting changes.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (action === "approve") {
        await approveTPRMResponseApi(assessmentId, comments.trim());
      } else {
        await requestTPRMResponseChangesApi(assessmentId, reason.trim());
      }
      await loadDetail();
      onChanged();
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        aria-labelledby="tprm-response-detail-title"
        aria-modal="true"
        className="my-auto max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2
              className="text-lg font-semibold text-slate-900 dark:text-white"
              id="tprm-response-detail-title"
            >
              Assessment response
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {assessmentId}
            </p>
          </div>
          <button
            aria-label="Close response details"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={onClose}
            type="button"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {loading && (
          <p className="text-sm text-slate-600 dark:text-slate-300" role="status">
            Loading response details...
          </p>
        )}
        {error && (
          <div className="mb-4 space-y-2" role="alert">
            <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
            {!loading && !detail && (
              <button
                className="rounded border border-slate-300 px-3 py-1.5 text-sm"
                onClick={loadDetail}
                type="button"
              >
                Retry
              </button>
            )}
          </div>
        )}
        {!loading && detail && (
          <div className="space-y-6">
            <dl className="grid grid-cols-1 gap-4 rounded-lg bg-slate-50 p-4 text-sm dark:bg-slate-800 sm:grid-cols-3">
              <DetailField label="Assessment" value={detail.assessment_name} />
              <DetailField label="Vendor" value={detail.vendor_name} />
              <DetailField label="Related service" value={detail.service_name} />
              <DetailField
                label="Questionnaire / version"
                value={`${formatValue(detail.template_snapshot?.name)} / ${formatValue(detail.template_version)}`}
              />
              <DetailField label="Status" value={detail.status} />
              <DetailField label="Created" value={formatDate(detail.created_at)} />
              <DetailField label="Due" value={formatDate(detail.due_date)} />
              <DetailField label="Submitted" value={formatDate(detail.submitted_at)} />
              <DetailField label="Completion" value={`${detail.completion_percentage ?? 0}%`} />
              <DetailField label="Business criticality" value={detail.business_criticality} />
              <DetailField label="Assessment risk score" value={detail.risk_score} />
              <DetailField label="Assessment risk tier" value={detail.risk_tier} />
              <DetailField label="Evidence review" value={detail.evidence_review_status} />
              <DetailField label="Reviewer" value={detail.reviewer} />
              <DetailField label="Owner" value={detail.owner} />
              <DetailField label="Last updated" value={formatDate(detail.updated_at)} />
            </dl>

            {detail.missing_requirements?.length > 0 && (
              <section aria-labelledby="response-missing-title">
                <h3
                  className="font-semibold text-slate-900 dark:text-white"
                  id="response-missing-title"
                >
                  Unanswered requirements
                </h3>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-800 dark:text-amber-200">
                  {detail.missing_requirements.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </section>
            )}

            <section aria-labelledby="response-questions-title">
              <h3
                className="font-semibold text-slate-900 dark:text-white"
                id="response-questions-title"
              >
                Question responses
              </h3>
              <div className="mt-2 space-y-3">
                {(detail.questions || []).map((question) => (
                  <article
                    className="rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                    key={question.question_id}
                  >
                    <h4 className="text-sm font-medium text-slate-900 dark:text-white">
                      {question.question_text || question.question_id}
                    </h4>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                      {formatValue(question.response)}
                    </p>
                    {question.comments && (
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                        Vendor comment: {question.comments}
                      </p>
                    )}
                    {question.evidence?.length > 0 && (
                      <ul className="mt-2 space-y-1 text-sm">
                        {question.evidence.map((item) => (
                          <li key={item.evidence_id}>
                            {item.filename} — {item.verification_status}
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}
              </div>
            </section>

            {detail.scoring_snapshot && (
              <section aria-labelledby="response-scoring-title">
                <h3
                  className="font-semibold text-slate-900 dark:text-white"
                  id="response-scoring-title"
                >
                  Scoring explanation
                </h3>
                <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                  Method: {formatValue(detail.scoring_snapshot.methodology)}; risk
                  score: {formatValue(detail.scoring_snapshot.risk_score)}; tier:{" "}
                  {formatValue(detail.scoring_snapshot.risk_level)}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-400">
                  {formatValue(detail.scoring_snapshot.explanation)}
                </p>
              </section>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <HistoryList
                title="Score history"
                items={detail.scoring_history || []}
                dateKey="calculated_at"
                valueKey="risk_score"
              />
              <HistoryList
                title="Submission history"
                items={detail.submission_history || []}
                dateKey="submitted_at"
                valueKey="submission_number"
              />
            </div>

            {detail.can_review && (
              <section
                aria-labelledby="response-review-title"
                className="rounded-lg border border-violet-200 p-4 dark:border-violet-900"
              >
                <h3
                  className="font-semibold text-slate-900 dark:text-white"
                  id="response-review-title"
                >
                  Assessment review
                </h3>
                <label className="mt-3 block text-sm font-medium">
                  Reviewer comments
                  <textarea
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white p-2 dark:border-slate-700 dark:bg-slate-950"
                    onChange={(event) => setComments(event.target.value)}
                    value={comments}
                  />
                </label>
                <label className="mt-3 block text-sm font-medium">
                  Reason to request changes
                  <textarea
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white p-2 dark:border-slate-700 dark:bg-slate-950"
                    onChange={(event) => setReason(event.target.value)}
                    value={reason}
                  />
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    className="rounded-lg bg-emerald-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
                    disabled={saving}
                    onClick={() => review("approve")}
                    type="button"
                  >
                    {saving ? "Saving..." : "Approve assessment"}
                  </button>
                  <button
                    className="rounded-lg border border-orange-300 px-3 py-2 text-sm font-medium text-orange-800 disabled:opacity-60 dark:text-orange-200"
                    disabled={saving || !reason.trim()}
                    onClick={() => review("changes")}
                    type="button"
                  >
                    Request changes
                  </button>
                </div>
              </section>
            )}
          </div>
        )}
        <div className="mt-5 flex justify-end">
          <button
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium dark:border-slate-700"
            onClick={onClose}
            type="button"
          >
            Close
          </button>
        </div>
      </section>
    </div>
  );
};

const HistoryList = ({ title, items, dateKey, valueKey }) => (
  <section>
    <h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>
    {items.length ? (
      <ol className="mt-2 space-y-2 text-sm text-slate-700 dark:text-slate-300">
        {items.map((item, index) => (
          <li className="rounded border border-slate-200 p-2 dark:border-slate-700" key={index}>
            {valueKey}: {formatValue(item[valueKey])}; {formatDate(item[dateKey])}
            {item.reviewer ? `; reviewer: ${item.reviewer}` : ""}
            {item.submitted_by ? `; submitted by: ${item.submitted_by}` : ""}
          </li>
        ))}
      </ol>
    ) : (
      <p className="mt-2 text-sm text-slate-500">No history recorded.</p>
    )}
  </section>
);

const ResponsesPage = () => {
  const [responses, setResponses] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [columnFilters, setColumnFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState("");
  const filterParams = useMemo(
    () => toResponseFilterParams(columnFilters),
    [columnFilters],
  );

  useEffect(() => {
    const initialAssessmentId = new URLSearchParams(window.location.search)
      .get("assessment_id");
    if (initialAssessmentId) setSelectedAssessmentId(initialAssessmentId);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadResponses = async () => {
      setLoading(true);
      setError("");
      try {
        const result = getResults(await getTPRMResponsesApi({
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          ...filterParams,
        }));
        if (!cancelled) {
          setResponses(result.results);
          setTotal(result.count);
        }
      } catch (requestError) {
        if (!cancelled) setError(errorText(requestError));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadResponses();
    return () => {
      cancelled = true;
    };
  }, [pagination.pageIndex, pagination.pageSize, filterParams, reloadKey]);

  const columns = useMemo(() => [
    {
      accessorKey: "assessment_id",
      header: "Assessment",
      meta: {
        filter: {
          variant: "text",
          label: "Assessment",
          placeholder: "Search assessment",
        },
      },
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{formatValue(row.original.assessment_name)}</div>
          <div className="text-xs text-slate-500">{formatValue(row.original.assessment_id)}</div>
        </div>
      ),
      size: 220,
    },
    {
      id: "vendor_id",
      accessorFn: (row) => row.vendor_id,
      header: "Vendor",
      cell: ({ row }) => `${formatValue(row.original.vendor_name)} (${formatValue(row.original.vendor_id)})`,
      meta: {
        filter: {
          variant: "text",
          label: "Vendor ID",
          placeholder: "Enter exact vendor ID",
        },
      },
    },
    { accessorKey: "service_name", header: "Related service", cell: ({ getValue }) => formatValue(getValue()) },
    {
      id: "questionnaire",
      header: "Questionnaire / version",
      accessorFn: (row) => `${formatValue(row.template_id)} / ${formatValue(row.template_version)}`,
    },
    { accessorKey: "created_at", header: "Created", cell: ({ getValue }) => formatDate(getValue()) },
    { accessorKey: "due_date", header: "Due", cell: ({ getValue }) => formatDate(getValue()) },
    {
      accessorKey: "submitted_at",
      header: "Submitted",
      cell: ({ getValue }) => formatDate(getValue()),
      meta: {
        filter: {
          variant: "date-range",
          label: "Submitted date",
        },
      },
    },
    {
      id: "completion",
      accessorFn: (row) => row.completion_percentage,
      header: "Completion",
      cell: ({ getValue }) => `${getValue() ?? 0}%`,
      meta: {
        filter: {
          variant: "select",
          label: "Completion",
          options: [
            { value: "complete", label: "Complete" },
            { value: "incomplete", label: "Incomplete" },
          ],
        },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          options: STATUS_OPTIONS.map((value) => ({ value, label: value })),
        },
      },
    },
    {
      id: "criticality",
      accessorFn: (row) => row.business_criticality,
      header: "Business criticality",
      cell: ({ getValue }) => formatValue(getValue()),
      meta: {
        filter: {
          variant: "select",
          label: "Business criticality",
          options: CRITICALITY_OPTIONS.map((value) => ({ value, label: value })),
        },
      },
    },
    { accessorKey: "risk_score", header: "Risk score", cell: ({ getValue }) => formatValue(getValue()) },
    {
      accessorKey: "risk_tier",
      header: "Risk tier",
      meta: {
        filter: {
          variant: "select",
          label: "Risk tier",
          options: RISK_TIER_OPTIONS.map((value) => ({ value, label: value })),
        },
      },
    },
    { accessorKey: "evidence_review_status", header: "Evidence / review" },
    { accessorKey: "reviewer", header: "Reviewer", cell: ({ getValue }) => formatValue(getValue()) },
    { accessorKey: "owner", header: "Owner", cell: ({ getValue }) => formatValue(getValue()) },
    { accessorKey: "updated_at", header: "Last updated", cell: ({ getValue }) => formatDate(getValue()) },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          aria-label={`View response ${formatValue(row.original.assessment_id)}`}
          className="inline-flex items-center rounded-lg border border-blue-200 p-2 text-blue-700 hover:bg-blue-50 dark:border-blue-900 dark:text-blue-300 dark:hover:bg-slate-800"
          onClick={() => setSelectedAssessmentId(row.original.assessment_id)}
          type="button"
        >
          <EyeIcon aria-hidden="true" className="h-4 w-4" />
        </button>
      ),
    },
  ], []);

  const handlePaginationChange = useCallback((updater) => {
    setPagination((current) =>
      typeof updater === "function" ? updater(current) : updater,
    );
  }, []);
  const closeDetails = () => {
    setSelectedAssessmentId("");
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has("assessment_id")) {
      searchParams.delete("assessment_id");
      const query = searchParams.toString();
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}`,
      );
    }
  };

  return (
    <>
      <section aria-labelledby="tprm-responses-heading" className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white" id="tprm-responses-heading">
            Assessment responses
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Review persisted vendor responses and assessment scoring. Business criticality is shown separately from assessed security risk.
          </p>
        </div>
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200" role="alert">
            <p>{error}</p>
            <button className="mt-2 underline" onClick={() => setReloadKey((value) => value + 1)} type="button">
              Retry loading responses
            </button>
          </div>
        )}
        <DataTable
          columns={columns}
          data={responses}
          emptyMessage="No assessment responses found."
          enableColumnFilters
          enableColumnVisibility
          enableGlobalFilter={false}
          enableSorting={false}
          getRowId={(row) => String(row.assessment_id)}
          loading={loading}
          manualFiltering
          manualPagination
          columnFilters={columnFilters}
          onColumnFiltersChange={(updater) => {
            const next = typeof updater === "function" ? updater(columnFilters) : updater;
            setColumnFilters(next);
            setPagination((current) => ({ ...current, pageIndex: 0 }));
          }}
          onPaginationChange={handlePaginationChange}
          pageCount={Math.ceil(total / pagination.pageSize)}
          pagination={pagination}
          rowCount={total}
        />
      </section>
      {selectedAssessmentId && (
        <ResponseDetailDialog
          assessmentId={selectedAssessmentId}
          onChanged={() => setReloadKey((value) => value + 1)}
          onClose={closeDetails}
        />
      )}
    </>
  );
};

export default ResponsesPage;
